import { cardLabel, newDecks, rankOf, type Card } from '../cards';
import { luckOf, secondChanceFrame } from './luck';
import type { Rng } from '../rng';
import type { CasinoGame, TableOption } from './types';

/**
 * Casino War: you and the dealer each get one card; higher wins 1:1 (aces
 * high). On a tie you choose: Surrender (get half your bet back) or Go to
 * War (put up the same again). In the war, beating the dealer wins the
 * raise while your original bet pushes; tying again wins the raise plus a
 * bonus equal to your bet. Six decks; ~2.9% edge when you always go to war.
 * Bets are even so the half-back on surrender is whole.
 */
const DECKS = 6;
const RESHUFFLE_BELOW = 60;
/** Bonus (x the original bet) for tying again in a war. */
export const TIE_BONUS = 1;
/** The ★'s luck: how often a dealer card that beats yours (on the deal or
 *  in a war) is drawn again, by luck level. Calibrated by sim to 103% /
 *  110%; pinned in luck.test.ts. */
export const SECOND_CHANCE = [0, 0.145, 0.37];

/** 0 (two) .. 12 (ace). */
export function warRank(card: Card): number {
  return (rankOf(card) + 12) % 13;
}

export interface WarState {
  phase: 'idle' | 'tie' | 'done';
  shoe: Card[];
  player: Card[];
  dealer: Card[];
  bet: number;
  wagered: number;
  returned: number;
  result: string;
  /** The table's luck for this hand. */
  luck?: number;
}

function draw(state: WarState, rng: Rng): Card {
  if (state.shoe.length === 0) state.shoe = rng.shuffle(newDecks(DECKS));
  return state.shoe.pop()!;
}

export const casinoWar: CasinoGame<WarState> = {
  id: 'war',
  usesLuck: true,
  name: 'Casino War',
  blurb: 'Higher card wins. On a tie, go to war or surrender.',
  baseMinBet: 6,
  baseMaxBet: 100,
  betStep: 2,
  specialPerk: 'Luck: a dealer card that beats yours may be redrawn.',
  jackpotRule: 'Tie a war on aces.',
  lockedHint: '(war or surrender)',
  create: () => ({ phase: 'idle', shoe: [], player: [], dealer: [], bet: 0, wagered: 0, returned: 0, result: '' }),
  locked: (state) => state.phase === 'tie',
  options(state, env): TableOption[] {
    if (state.phase !== 'tie') return [{ id: 'deal', label: state.phase === 'done' ? 'Deal again' : 'Deal' }];
    const opts: TableOption[] = [];
    if (env.bankroll >= state.bet) opts.push({ id: 'war', label: `Go to war (+$${state.bet})` });
    opts.push({ id: 'surrender', label: `Surrender (-$${state.bet / 2})` });
    return opts;
  },
  describe(state) {
    if (state.phase === 'idle') return 'One card each. Higher wins; aces high.';
    const cards = `You: ${state.player.map(cardLabel).join(' → ')}\nDealer: ${state.dealer.map(cardLabel).join(' → ')}`;
    if (state.phase === 'tie') {
      const bonus = TIE_BONUS;
      return `${cards}\n\nTie! War: win the raise; tie again for a ${bonus}x bonus.`;
    }
    return `${cards}\n\n${state.result}`;
  },
  act(prev, optionId, env) {
    const state: WarState = { ...prev, shoe: [...prev.shoe] };
    if (state.phase !== 'tie') {
      if (optionId !== 'deal') return { state: prev, bankroll: env.bankroll };
      if (state.shoe.length < RESHUFFLE_BELOW) state.shoe = env.rng.shuffle(newDecks(DECKS));
      const luck = luckOf(env);
      const player = [draw(state, env.rng)];
      const dealer = [draw(state, env.rng)];
      // The ★'s luck: a dealer card that beats yours is sometimes redrawn.
      let lucky = false;
      const faceDown = `You: ${cardLabel(player[0]!)}\nDealer: ??`;
      const frames = [faceDown];
      if (warRank(dealer[0]!) > warRank(player[0]!) && env.rng.next() < (SECOND_CHANCE[luck] ?? 0)) {
        const beat = dealer[0]!;
        dealer[0] = draw(state, env.rng);
        lucky = warRank(dealer[0]!) <= warRank(player[0]!);
        if (lucky) frames.push(secondChanceFrame(`You: ${cardLabel(player[0]!)}\nDealer: ${cardLabel(beat)}`), faceDown);
      }
      const bet = env.bet;
      const bankroll = env.bankroll - bet;
      const diff = warRank(player[0]!) - warRank(dealer[0]!);
      if (diff === 0) return { state: { ...state, phase: 'tie', player, dealer, bet, wagered: bet, returned: 0, result: '', luck }, bankroll, frames };
      const returned = diff > 0 ? bet * 2 : 0;
      const result = diff > 0 ? `${lucky ? '★ ' : ''}You win +$${bet}` : `Dealer wins -$${bet}`;
      return {
        state: { ...state, phase: 'done', player, dealer, bet, wagered: bet, returned, result, luck },
        bankroll: bankroll + returned,
        settled: { wagered: bet, returned },
        frames,
      };
    }
    if (optionId === 'surrender') {
      const returned = state.bet / 2;
      return {
        state: { ...state, phase: 'done', returned, result: `Surrendered -$${state.bet / 2}` },
        bankroll: env.bankroll + returned,
        settled: { wagered: state.bet, returned },
      };
    }
    if (optionId !== 'war' || env.bankroll < state.bet) return { state: prev, bankroll: env.bankroll };
    // War: burn three, then one card each.
    for (let i = 0; i < 3; i++) draw(state, env.rng);
    const p = draw(state, env.rng);
    let d = draw(state, env.rng);
    let lucky = false;
    const lines = (you: string, them: string) => `You: ${[...state.player.map(cardLabel), you].join(' → ')}\nDealer: ${[...state.dealer.map(cardLabel), them].join(' → ')}`;
    const frames = ['Burn', 'Burn, burn', 'Burn, burn, burn'].map((b) => `${lines('?', '?')}\n\n${b}...`);
    frames.push(lines(cardLabel(p), '??'));
    if (warRank(d) > warRank(p) && env.rng.next() < (SECOND_CHANCE[state.luck ?? 0] ?? 0)) {
      const beat = d;
      d = draw(state, env.rng);
      lucky = warRank(d) <= warRank(p);
      if (lucky) frames.push(secondChanceFrame(lines(cardLabel(p), cardLabel(beat))), lines(cardLabel(p), '??'));
    }
    const player = [...state.player, p];
    const dealer = [...state.dealer, d];
    const raise = state.bet;
    const bankroll = env.bankroll - raise;
    const diff = warRank(p) - warRank(d);
    const bonus = TIE_BONUS * state.bet;
    let returned = 0;
    let result = `Lost the war -$${state.bet + raise}`;
    if (diff > 0) {
      returned = state.bet + raise * 2;
      result = `${lucky ? '★ ' : ''}Won the war +$${raise}`;
    } else if (diff === 0) {
      returned = state.bet + raise * 2 + bonus;
      result = `Tied again! +$${raise + bonus}`;
    }
    return {
      state: { ...state, phase: 'done', player, dealer, wagered: state.bet + raise, returned, result },
      bankroll: bankroll + returned,
      settled: { wagered: state.bet + raise, returned },
      jackpot: diff === 0 && warRank(p) === 12,
      frames,
    };
  },
};
